import React, { useCallback, useRef, useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { Surface } from './ui';

/**
 * SubscriptionGrantPanel — the ONLY way to hand a user a paid plan from Admin.
 *
 * It replaces a plain SelectInput on `subscriptionTier`. That dropdown was not
 * merely unaudited, it wrote the WRONG FIELD: `subscriptionTier` is the PAID
 * claim, so setting it fabricated a paying customer. Measured against the live
 * entitlement resolver:
 *
 *   subscriptionTier=PRO  -> source "paid",    isPaid TRUE,  expiresAt null
 *   grantedTier=PRO       -> source "granted", isPaid FALSE, expiresAt as set
 *
 * isPaid TRUE is a lie with consequences: decision 8 requires the app to
 * distinguish a PAID Pro from an EARNED or GRANTED one, revenue reporting
 * counts paid tiers, and a fabricated paid claim has no Stripe subscription
 * behind it to ever expire or be reconciled. This panel posts to the audited
 * grant endpoints instead, which write `grantedTier`.
 *
 * The PIN gate is deliberately the SAME one the Pricing page uses, not a new
 * one: granting Pro forever must not be easier than editing a price.
 */

const GRANTS_API = `${API_BASE_URL}/adminSubscriptionGrants`;
const EDIT_AUTH_API = `${API_BASE_URL}/platformPricing/edit-authorization`;
const EDIT_TOKEN_HEADER = 'X-Pricing-Edit-Token';

/*
 * FREE is absent on purpose. Removing a plan is a REVOKE, which is a different
 * audited action with different consequences, not "granting the FREE tier".
 * Offering FREE here would let an admin think they had removed a grant when
 * they had actually recorded granting one.
 */
export const GRANTABLE_TIERS = [
    { id: 'PLUS', name: 'Plus ($9.99/mo value)' },
    { id: 'PRO', name: 'Pro ($29.99/mo value)' },
    { id: 'BUSINESS', name: 'Business ($49.99/mo value)' },
];

/** Minimum characters of justification. A grant with no stated reason is unauditable. */
export const MIN_REASON_LENGTH = 10;

const styles = {
    warn: { background: '#fff8e6', border: '1px solid #f0d9a0', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#6b5618', marginBottom: 12 },
    row: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' },
    field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 },
    label: { fontSize: 12, fontWeight: 700, color: '#566071' },
    input: { padding: '8px 10px', border: '1px solid #cfd5df', borderRadius: 8, fontSize: 14 },
    reason: { padding: '8px 10px', border: '1px solid #cfd5df', borderRadius: 8, fontSize: 14, minHeight: 60, width: '100%' },
    btn: { padding: '9px 16px', borderRadius: 8, border: 'none', fontWeight: 700, cursor: 'pointer' },
    grant: { background: '#1f6feb', color: '#fff' },
    revoke: { background: '#fff', color: '#b42318', border: '1px solid #e4a6a0' },
    err: { color: '#b42318', fontSize: 12, marginTop: 6, minHeight: 16 },
};

/**
 * Normalises the many shapes an httpClient rejection can take into a code.
 * Without this the UI shows "[object Object]" on the exact failures an admin
 * most needs to read: a refused PIN, or a refused grant.
 */
export const extractErrorCode = (err) => {
    if (!err) return null;
    const body = err.body || err.response || {};
    return body.code || body.error || err.code || null;
};

/** Maps a server refusal to words an admin can act on. */
export const messageForCode = (code, fallback) => {
    switch (code) {
        case 'REASON_REQUIRED': return 'A reason is required, and must be specific enough to audit.';
        case 'SELF_GRANT': return 'You cannot grant a plan to yourself.';
        case 'TARGET_NOT_FOUND': return 'That user no longer exists.';
        case 'UNSUPPORTED_TIER': return 'That plan cannot be granted.';
        case 'NOT_A_PARTNER': return 'That user has no grant to revoke.';
        case 'FORBIDDEN': return 'Your admin role cannot grant plans.';
        case 'TOO_MANY_REQUESTS': return 'Too many attempts. Wait a moment and try again.';
        case 'INVALID_PIN': return 'Incorrect PIN.';
        default: return fallback || 'The request failed. Nothing was changed.';
    }
};

const SubscriptionGrantPanel = ({ userId, currentGrantedTier }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const editTokenRef = useRef(null);

    const [tier, setTier] = useState('PRO');
    const [until, setUntil] = useState('');
    const [reason, setReason] = useState('');
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const reasonTooShort = reason.trim().length < MIN_REASON_LENGTH;

    /*
     * The PIN is exchanged for a short-lived token on every submit rather than
     * being held across the session: an unlocked panel left open on a shared
     * admin machine is the same hole as no PIN at all.
     */
    const withToken = useCallback(async () => {
        if (!/^\d{4}$/.test(pin)) {
            throw Object.assign(new Error('pin'), { body: { code: 'INVALID_PIN' } });
        }
        const { json } = await httpClient(EDIT_AUTH_API, {
            method: 'POST',
            body: JSON.stringify({ pin }),
        });
        if (!json || !json.editToken) {
            throw Object.assign(new Error('pin'), { body: { code: 'INVALID_PIN' } });
        }
        editTokenRef.current = json.editToken;
        return json.editToken;
    }, [pin]);

    const submit = useCallback(async (action) => {
        setError('');
        setBusy(true);
        try {
            const token = await withToken();
            const headers = new Headers({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                [EDIT_TOKEN_HEADER]: token,
            });

            if (action === 'grant') {
                await httpClient(GRANTS_API, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        userId,
                        tier,
                        /*
                         * Empty means permanent, and the server is told so
                         * EXPLICITLY. Omitting the field would let a default
                         * decide, and both possible defaults are wrong in
                         * opposite directions.
                         */
                        grantedUntil: until ? new Date(until).toISOString() : null,
                        reason: reason.trim(),
                    }),
                });
                notify(
                    until
                        ? `Granted ${tier} until ${new Date(until).toLocaleDateString('en-GB')}.`
                        : `Granted ${tier} with NO EXPIRY.`,
                    { type: 'info' }
                );
            } else {
                await httpClient(`${GRANTS_API}/revoke`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ userId, reason: reason.trim() }),
                });
                notify('Grant revoked.', { type: 'info' });
            }
            setReason('');
            setPin('');
            setUntil('');
            refresh();
        } catch (err) {
            const code = extractErrorCode(err);
            setError(messageForCode(code, err && err.message));
        } finally {
            setBusy(false);
        }
    }, [withToken, userId, tier, until, reason, notify, refresh]);

    /*
     * Surface, not a hand-rolled bordered div: CLAUDE.md requires admin panels
     * to sit inside sk-surface, and SPEC 25 forbids inventing new design
     * furniture for this feature. Surface also supplies the title and
     * description slots this panel was duplicating by hand.
     */
    return (
        <Surface
            title="Subscription grant"
            description="Give a user a paid plan's benefits without charging them."
            className="sk-grant-panel"
        >
          <div data-testid="grant-panel">
            <div style={styles.warn} data-testid="grant-warning">
                A grant gives the plan&apos;s benefits <strong>without charging</strong>. It is
                recorded against your admin account with the reason you type below.
                {!until && ' Leaving the end date empty grants the plan PERMANENTLY.'}
            </div>

            {currentGrantedTier ? (
                <div style={{ fontSize: 13, marginBottom: 10 }} data-testid="grant-current">
                    Current grant: <strong>{currentGrantedTier}</strong>
                </div>
            ) : null}

            <div style={styles.row}>
                <div style={styles.field}>
                    <label style={styles.label} htmlFor="grantTier">Plan</label>
                    <select
                        id="grantTier"
                        style={styles.input}
                        value={tier}
                        onChange={e => setTier(e.target.value)}
                        data-testid="grant-tier"
                    >
                        {GRANTABLE_TIERS.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
                <div style={styles.field}>
                    <label style={styles.label} htmlFor="grantUntil">Ends (empty = permanent)</label>
                    <input
                        id="grantUntil"
                        type="date"
                        style={styles.input}
                        value={until}
                        onChange={e => setUntil(e.target.value)}
                        data-testid="grant-until"
                    />
                </div>
                <div style={styles.field}>
                    <label style={styles.label} htmlFor="grantPin">Edit PIN</label>
                    <input
                        id="grantPin"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        style={styles.input}
                        value={pin}
                        onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                        data-testid="grant-pin"
                    />
                </div>
            </div>

            <div style={{ marginTop: 12 }}>
                <label style={styles.label} htmlFor="grantReason">
                    Reason (required, at least {MIN_REASON_LENGTH} characters)
                </label>
                <textarea
                    id="grantReason"
                    style={styles.reason}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="e.g. Strategic partner agreement signed 4 Nov, approved by Jalal"
                    data-testid="grant-reason"
                />
            </div>

            <div style={styles.err} role="alert" data-testid="grant-error">{error}</div>

            <div style={{ ...styles.row, marginTop: 4 }}>
                <button
                    type="button"
                    style={{ ...styles.btn, ...styles.grant, opacity: (busy || reasonTooShort) ? 0.5 : 1 }}
                    disabled={busy || reasonTooShort}
                    onClick={() => submit('grant')}
                    data-testid="grant-submit"
                >
                    {busy ? 'Working…' : 'Grant plan'}
                </button>
                <button
                    type="button"
                    style={{ ...styles.btn, ...styles.revoke, opacity: (busy || reasonTooShort) ? 0.5 : 1 }}
                    disabled={busy || reasonTooShort}
                    onClick={() => submit('revoke')}
                    data-testid="grant-revoke"
                >
                    Revoke grant
                </button>
            </div>
          </div>
        </Surface>
    );
};

export default SubscriptionGrantPanel;
